import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS } from './constants';
import TopSoccerGamesCarousel from './TopSoccerGamesCarousel';
import { Header, Card, Button, CrownedELXR } from './components/ui';
import { tokens } from './styles/tokens';
import { useResponsive } from './hooks/useResponsive';
import responsiveStyles from './styles/responsive.module.css';

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthenticated, 
    loading, 
    logout, 
    userInfo,
    getAccessToken
  } = useAuth();
  const [successMessage, setSuccessMessage] = useState('');
  const [bets, setBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);
  
  // Responsive hook for mobile detection
  const { isMobile } = useResponsive();

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);



  // Show success message if coming from bet placement or verification
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch user's betting data
  const fetchUserBets = async () => {
    try {
      setLoadingBets(true);
      const accessToken = getAccessToken();
      
      if (!accessToken) {
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.USER_API}/bet`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBets(Array.isArray(data) ? data : data.bets || []);
      } else if (response.status === 404) {
        setBets([]);
      }
      
    } catch (error) {
      console.error('Error fetching user bets:', error);
      setBets([]);
    } finally {
      setLoadingBets(false);
    }
  };

  // Fetch bets when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserBets();
    }
  }, [isAuthenticated]);

  // Show loading state
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: tokens.colors.dark[900]
      }}>
        <Card variant="elevated" padding="large">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ 
              background: tokens.colors.gradients.glow,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: tokens.spacing[4]
            }}>
              ELXR Sportsbook
            </h2>
            <div className="skeleton" style={{ 
              width: '200px', 
              height: '20px', 
              borderRadius: tokens.borderRadius.md,
              margin: '0 auto'
            }} />
          </div>
        </Card>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    { label: 'Home', href: '/home', active: true },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div 
      className={isMobile ? responsiveStyles.homeMobileContainer : ''}
      style={{ minHeight: '100vh', background: tokens.colors.dark[900] }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />

      {/* Hero Section */}
      <section 
        className={isMobile ? responsiveStyles.homeMobileHero : ''}
        style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]} 0%, ${tokens.colors.dark[900]} 100%)`,
          padding: `${tokens.spacing[12]} 0 ${tokens.spacing[8]}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${tokens.colors.primary[900]}22 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, ${tokens.colors.secondary[900]}22 0%, transparent 50%)`,
          opacity: 0.5
        }} />
        
        <div 
          className={isMobile ? responsiveStyles.homeMobileHeroContent : ''}
          style={{ 
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem',
            position: 'relative', 
            zIndex: 1 
          }}>
          <h1 
            className={isMobile ? responsiveStyles.homeMobileTitle : ''}
            style={{
              fontSize: tokens.typography.fontSize['5xl'],
              fontWeight: tokens.typography.fontWeight.extrabold,
              background: tokens.colors.gradients.glow,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: tokens.spacing[3],
              filter: 'drop-shadow(0 0 30px rgba(34, 197, 94, 0.3))'
            }}>
            Welcome to <CrownedELXR /> Sportsbook
          </h1>
          <p 
            className={isMobile ? responsiveStyles.homeMobileSubtitle : ''}
            style={{
              fontSize: tokens.typography.fontSize.xl,
              color: tokens.colors.neutral[400],
              marginBottom: tokens.spacing[6]
            }}>
            The premier XRP-based Sportsbook
          </p>
        </div>
      </section>

      {/* Games Section - Featured Carousel */}
      <section 
        className={isMobile ? responsiveStyles.homeMobileGamesSection : ''}
        style={{ 
          padding: `${tokens.spacing[8]} 0 ${tokens.spacing[16]}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          {successMessage && (
            <div 
              className={isMobile ? responsiveStyles.homeMobileSuccessMessage : ''}
              style={{
                textAlign: 'center',
                marginBottom: tokens.spacing[8]
              }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.spacing[2],
                padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
                background: `${tokens.colors.accent[900]}33`,
                border: `1px solid ${tokens.colors.accent[700]}`,
                borderRadius: tokens.borderRadius.full,
                color: tokens.colors.accent[400],
                fontSize: tokens.typography.fontSize.lg,
                fontWeight: tokens.typography.fontWeight.semibold,
                animation: 'fadeIn 300ms ease'
              }}>
                <span>{successMessage}</span>
              </div>
            </div>
          )}
          <TopSoccerGamesCarousel />
        </div>
      </section>

      {/* Quick Stats Section */}
      <section 
        className={isMobile ? responsiveStyles.homeMobileStats : ''}
        style={{ 
          background: tokens.colors.dark[800],
          padding: `${tokens.spacing[16]} 0` 
        }}>
        <div 
          className={isMobile ? responsiveStyles.homeMobileStatsContainer : ''}
          style={{ 
            position: 'relative', 
            zIndex: 1,
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem'
          }}>
          <div 
            className={isMobile ? responsiveStyles.homeMobileStatsGrid : ''}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: tokens.spacing[6]
            }}>
            <Card 
              variant="gradient" 
              hoverable 
              className={isMobile ? responsiveStyles.homeMobileStatsCard : ''}
              style={{ marginLeft: tokens.spacing[8] }}>
              <Card.Body>
                <h3 
                  className={isMobile ? responsiveStyles.homeMobileStatsTitle : ''}
                  style={{ 
                    color: tokens.colors.primary[400],
                    marginBottom: tokens.spacing[2],
                    fontSize: tokens.typography.fontSize.lg
                  }}>
                  Active Bets
                </h3>
                <p 
                  className={isMobile ? responsiveStyles.homeMobileStatsValue : ''}
                  style={{ 
                    fontSize: tokens.typography.fontSize['3xl'],
                    fontWeight: tokens.typography.fontWeight.bold,
                    background: tokens.colors.gradients.primary,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                  {loadingBets ? '...' : bets.filter(bet => (bet.status || 'pending').toLowerCase() === 'pending').length}
                </p>
              </Card.Body>
            </Card>
            
            <Card 
              variant="gradient" 
              hoverable
              className={isMobile ? responsiveStyles.homeMobileStatsCard : ''}>
              <Card.Body>
                <h3 
                  className={isMobile ? responsiveStyles.homeMobileStatsTitle : ''}
                  style={{ 
                    color: tokens.colors.secondary[400],
                    marginBottom: tokens.spacing[2],
                    fontSize: tokens.typography.fontSize.lg
                  }}>
                  Total Winnings
                </h3>
                <p 
                  className={isMobile ? responsiveStyles.homeMobileStatsValue : ''}
                  style={{ 
                    fontSize: tokens.typography.fontSize['3xl'],
                    fontWeight: tokens.typography.fontWeight.bold,
                    background: tokens.colors.gradients.secondary,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                  {loadingBets ? '...' : 
                    (() => {
                      const wonBets = bets.filter(bet => (bet.status || '').toLowerCase() === 'won');
                      const totalWinnings = wonBets.reduce((sum, bet) => {
                        const amount = parseFloat(bet.amount || 0);
                        const odds = parseFloat(bet.odds || 0);
                        const payout = amount * odds; // Calculate payout from amount * odds
                        return sum + (payout - amount); // Profit only, not total payout
                      }, 0);
                      return `${totalWinnings.toFixed(2)} XRP`;
                    })()
                  }
                </p>
              </Card.Body>
            </Card>
            
            <Card 
              variant="gradient" 
              hoverable 
              className={isMobile ? responsiveStyles.homeMobileStatsCard : ''}
              style={{ marginRight: tokens.spacing[8] }}>
              <Card.Body>
                <h3 
                  className={isMobile ? responsiveStyles.homeMobileStatsTitle : ''}
                  style={{ 
                    color: tokens.colors.accent[400],
                    marginBottom: tokens.spacing[2],
                    fontSize: tokens.typography.fontSize.lg
                  }}>
                  Win Rate
                </h3>
                <p 
                  className={isMobile ? responsiveStyles.homeMobileStatsValue : ''}
                  style={{ 
                    fontSize: tokens.typography.fontSize['3xl'],
                    fontWeight: tokens.typography.fontWeight.bold,
                    background: tokens.colors.gradients.accent,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                  {loadingBets ? '...' : 
                    (() => {
                      const settledBets = bets.filter(bet => {
                        const status = (bet.status || '').toLowerCase();
                        return status === 'won' || status === 'lost';
                      });
                      if (settledBets.length === 0) return '--%';
                      const wonBets = settledBets.filter(bet => (bet.status || '').toLowerCase() === 'won');
                      const winRate = Math.round((wonBets.length / settledBets.length) * 100);
                      return `${winRate}%`;
                    })()
                  }
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage; 